/**
 * Parse suggested responses from LLM output that may contain <suggested_actions> XML tags
 * Returns cleaned content and extracted suggestions.
 *
 * IMPORTANT: This function must NOT alter character positions in the returned
 * content (e.g. via .trim()) because tool-call markers recorded during
 * streaming are byte-offset indices into the *original* accumulated text.
 * Trimming would shift those positions and cause post-tool characters to
 * render before the tool badge instead of after it.
 */
export function parseSuggestedResponses(content: string): { content: string; suggestions: string[] } {
  if (!content || typeof content !== 'string') {
    return { content: content || '', suggestions: [] };
  }
  
  // Pattern to match <suggested_actions>...</suggested_actions>
  const pattern = /<suggested_actions>(.*?)<\/suggested_actions>/s;
  const match = content.match(pattern);
  
  if (!match) {
    return { content, suggestions: [] };
  }
  
  // Extract the content inside the tags
  const suggestedActionsContent = match[1];
  
  // Parse individual <sa>...</sa> tags
  const saPattern = /<sa>(.*?)<\/sa>/gs;
  const saMatches = [...suggestedActionsContent.matchAll(saPattern)];
  
  // Clean up the suggestions (strip whitespace) and limit to max 3
  const suggestions = saMatches
    .map(m => m[1]?.trim())
    .filter(s => s && s.length > 0)
    .slice(0, 3);
  
  // Remove the suggested_actions section from the content.
  // The <suggested_actions> block always appears at the very end of the
  // response, after all tool markers, so removing it does not shift any
  // marker positions.
  const cleanedContent = content.replace(pattern, '');

  return { content: cleanedContent, suggestions };
}

